import { FC } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useNavigate } from 'react-router-dom';
import { Character } from '../../data/characters';
import { motion } from 'framer-motion';

interface WinnerScreenProps {
  winner: 'player' | 'opponent';
  character: Character | null;
  onPlayAgain: () => void;
  isRecordingWin?: boolean;
  playerStats?: { wins: number; isWhitelisted: boolean } | null;
}

const WinnerScreen: FC<WinnerScreenProps> = ({
  winner,
  character,
  onPlayAgain,
  isRecordingWin = false,
  playerStats = null
}) => {
  const { publicKey } = useWallet();
  const navigate = useNavigate();
  const walletAddress = publicKey?.toBase58() || '';

  if (!character) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      className="fixed inset-0 flex items-center justify-center z-50 bg-black/80 backdrop-blur-sm"
    >
      <div className="bg-gaming-dark p-8 rounded-xl border border-primary-500/30 max-w-lg w-full mx-4">
        <div className="text-center">
          <h2 className="text-4xl font-bold mb-4 code-mono">
            {winner === 'player' ? 'Victory!' : 'Defeat'}
          </h2>
          <p className="text-gray-400 mb-6 code-mono">
            {winner === 'player'
              ? 'Congratulations! You have emerged victorious!'
              : 'Better luck next time! Keep practicing and try again.'}
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-black/30 p-4 rounded-lg">
              <div className="text-primary-400 text-sm mb-1 code-mono">Damage Dealt</div>
              <div className="text-2xl font-bold code-mono">{stats.damageDealt}</div>
            </div>
            <div className="bg-black/30 p-4 rounded-lg">
              <div className="text-primary-400 text-sm mb-1 code-mono">Turns Taken</div>
              <div className="text-2xl font-bold code-mono">{stats.turnsTaken}</div>
            </div>
            <div className="bg-black/30 p-4 rounded-lg">
              <div className="text-primary-400 text-sm mb-1 code-mono">Critical Hits</div>
              <div className="text-2xl font-bold code-mono">{stats.criticalHits}</div>
            </div>
            <div className="bg-black/30 p-4 rounded-lg">
              <div className="text-primary-400 text-sm mb-1 code-mono">Abilities Used</div>
              <div className="text-2xl font-bold code-mono">{stats.abilitiesUsed}</div>
            </div>
          </div>

          {/* Rewards (if winner) */}
          {winner === 'player' && (
            <div className="bg-primary-500/20 p-4 rounded-lg mb-8 border border-primary-500/30">
              <h3 className="text-primary-400 text-lg mb-2 code-mono">Rewards</h3>
              <div className="flex justify-center items-center gap-4">
                <div>
                  <div className="text-sm text-gray-400 code-mono">Experience</div>
                  <div className="text-xl font-bold text-primary-100 code-mono">+{rewards.xp} XP</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 code-mono">Tokens</div>
                  <div className="text-xl font-bold text-yellow-400 code-mono">+{rewards.tokens}</div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-center gap-4">
            <button
              onClick={onPlayAgain}
              className="px-6 py-2 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors code-mono"
            >
              Play Again
            </button>
            <button
              onClick={onExit}
              className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors code-mono"
            >
              Exit Arena
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default WinnerScreen; 